// =======================
// sear/src/paginas/paginaEscola/perfil/infoPerfilMant.js
// =======================

import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ListaAtos from "./atosMant";
import { api } from "@api/api";
import TabelaVinculosInstitucionais from "./componente/TabelaVinculosInstitucionais";
import { coresTextosGlobais, bordaSuperiorModalGlobal, tamanhosControlesGlobais, tamanhosPaginasAdministrativas } from "../../../estilos/global";

const formatTelefone = (telefone) => {
  if (!telefone) return "";

  const cleaned = telefone.replace(/\D/g, "");

  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return telefone;
};

const formatCEP = (cep) => {
  if (!cep) return "";

  return cep.replace(/(\d{5})(\d{3})/, "$1-$2");
};

const normalizarTexto = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const mascararCNPJ = (cnpj) => {
  if (!cnpj) return "Não informado";

  return "**.***.***/****-**";
};

function ModalAtosMantenedora({ open, onClose }) {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        onClick={onClose}
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <motion.div
          onClick={(event) => event.stopPropagation()}
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.2,
          }}
          style={{
            width: "100%",
            maxWidth: 900,
          }}
        >
          <Paper
            sx={{
              position: "relative",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: {
                xs: 2,
                md: 3,
              },
              borderRadius: 2,
              outline: "none",
              ...bordaSuperiorModalGlobal,
            }}
          >
            <IconButton
              onClick={onClose}
              aria-label="Fechar"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: "var(--sear-texto-secundario, #5f6f94)",
                zIndex: 1,

                "&:hover": {
                  color: "var(--sear-azul, #0767d8)",
                  backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <ListaAtos />
          </Paper>
        </motion.div>
      </Box>
    </Modal>
  );
}

function ModalMantenedora({ open, onClose, mantenedora, onOpenAtos }) {
  if (!mantenedora) return null;

  const escolas = Array.isArray(mantenedora.escolas) ? mantenedora.escolas : [];

  const etapasModalidades = Array.isArray(mantenedora.etapasModalidades)
    ? mantenedora.etapasModalidades
    : [];

  const endereco =
    [
      [mantenedora.rua, mantenedora.numero]
        .filter(
          (valor) => valor !== null && valor !== undefined && valor !== "",
        )
        .join(", "),
      mantenedora.bairro,
    ]
      .filter(Boolean)
      .join(" — ") || "Não informado";

  const municipioUF =
    [mantenedora.cidade, mantenedora.estado].filter(Boolean).join(" — ") ||
    "Não informado";

  const campos = [
    ["CNPJ", mascararCNPJ(mantenedora.cnpj)],
    ["Endereço", endereco],
    ["Município/UF", municipioUF],
    ["CEP", formatCEP(mantenedora.cep) || "Não informado"],
    ["Telefone", formatTelefone(mantenedora.telefone) || "Não informado"],
    ["E-mail", mantenedora.email || "Não informado"],
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        onClick={onClose}
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <motion.div
          onClick={(event) => event.stopPropagation()}
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.2,
          }}
          style={{
            width: "100%",
            maxWidth: 590,
          }}
        >
          <Paper
            sx={{
              position: "relative",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: {
                xs: 2,
                md: 3,
              },
              borderRadius: 2,
              outline: "none",
              ...bordaSuperiorModalGlobal,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "var(--sear-azul, #3156a4)",
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                paddingRight: 5,
              }}
            >
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  color: "var(--sear-azul, #087cf0)",
                  backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
                }}
              >
                <AccountBalanceRoundedIcon />
              </Avatar>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: coresTextosGlobais.tituloPagina,
                    fontWeight: 700,
                    fontSize: tamanhosPaginasAdministrativas.tituloPagina,
                    lineHeight: 1.15,
                  }}
                >
                  {mantenedora.nome}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "var(--sear-texto-secundario, #7b8798)",
                    fontSize: tamanhosControlesGlobais.descricao,
                    lineHeight: 1.35,
                  }}
                >
                  Dados institucionais e vínculos
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                marginY: 2,
                borderColor: "var(--sear-borda, #c9def4)",
              }}
            />

            <Typography
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontWeight: 700,
                marginBottom: 1.5,
              }}
            >
              Dados da Mantenedora
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {campos.map(([rotulo, valor]) => (
                <Box key={rotulo}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--sear-texto-secundario, #5f6f94)",
                    }}
                  >
                    {rotulo}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--sear-texto, #33446b)",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {valor}
                  </Typography>
                </Box>
              ))}
            </Box>

            {etapasModalidades.length > 0 && (
              <>
                <Divider
                  sx={{
                    marginY: 2,
                    borderColor: "var(--sear-borda, #c9def4)",
                  }}
                />

                <Typography
                  sx={{
                    color: "var(--sear-azul, #0767d8)",
                    fontWeight: 700,
                    marginBottom: 1,
                  }}
                >
                  Etapas e modalidades mantidas
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {etapasModalidades.map((item) => {
                    const nome = typeof item === "string" ? item : item.nome;

                    return (
                      <Chip
                        key={nome}
                        label={nome}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "var(--sear-azul, #0767d8)",
                          borderColor: "var(--sear-borda-forte, #087cf0)",
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            )}

            <Divider
              sx={{
                marginY: 2,
                borderColor: "var(--sear-borda, #c9def4)",
              }}
            />

            <Typography
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontWeight: 700,
                marginBottom: 1,
              }}
            >
              Escolas vinculadas
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 1,
              }}
            >
              {escolas.map((escola) => {
                const modalidades = Array.isArray(escola.modalidades)
                  ? escola.modalidades
                  : escola.modalidade
                    ? [escola.modalidade]
                    : [];

                return (
                  <Paper
                    key={escola.id_procura_atos || escola.id}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      padding: 1.25,
                      borderColor: "var(--sear-borda, #c9def4)",
                      boxShadow: "none",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        color: "var(--sear-azul, #087cf0)",
                        backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
                      }}
                    >
                      <SchoolRoundedIcon fontSize="small" />
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "var(--sear-azul, #3156a4)",
                          fontWeight: 700,
                        }}
                      >
                        {escola.nome}
                      </Typography>

                      {modalidades.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "var(--sear-texto-secundario, #5f6f94)",
                          }}
                        >
                          {modalidades.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                );
              })}

              {escolas.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "var(--sear-texto-secundario, #5f6f94)",
                  }}
                >
                  Nenhuma escola vinculada encontrada.
                </Typography>
              )}
            </Box>

            <Divider
              sx={{
                marginY: 2,
                borderColor: "var(--sear-borda, #c9def4)",
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                startIcon={<DescriptionOutlinedIcon />}
                onClick={onOpenAtos}
                sx={{
                  minWidth: 110,
                  textTransform: "none",
                  backgroundColor: "var(--sear-acao-azul, #087cf0)",
                  boxShadow: "none",
                }}
              >
                Ver atos
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Modal>
  );
}

export default function MantenedoraComponent({ ativo = true, busca = "" } = {}) {
  const [mantenedoras, setMantenedoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalDetalhesOpen, setModalDetalhesOpen] = useState(false);
  const [modalAtosOpen, setModalAtosOpen] = useState(false);
  const [mantenedoraSelecionada, setMantenedoraSelecionada] = useState(null);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [respostaMantenedora, respostaEscolas] = await Promise.all([
          fetch(api.maintainerData(), {
            credentials: "include",
            headers,
          }),

          fetch(api.listUserSchools(), {
            credentials: "include",
            headers,
          }),
        ]);

        if (!respostaMantenedora.ok) {
          const erro = await respostaMantenedora.json();

          throw new Error(
            erro.message || `Erro: ${respostaMantenedora.status}`,
          );
        }

        const dadosMantenedora = await respostaMantenedora.json();

        const dadosEscolas = respostaEscolas.ok
          ? await respostaEscolas.json()
          : { escolas: [] };

        const escolasRecebidas = Array.isArray(dadosEscolas?.escolas)
          ? dadosEscolas.escolas
          : [];

        const escolasAgrupadas = Object.values(
          escolasRecebidas.reduce((acc, escola) => {
            const chave = escola.id_procura_atos || escola.id;

            const modalidadesRecebidas = Array.isArray(escola.modalidades)
              ? escola.modalidades
              : escola.modalidade
                ? [escola.modalidade]
                : [];

            if (!acc[chave]) {
              acc[chave] = {
                ...escola,
                modalidades: [...modalidadesRecebidas],
              };

              return acc;
            }

            modalidadesRecebidas.forEach((modalidade) => {
              if (!acc[chave].modalidades.includes(modalidade)) {
                acc[chave].modalidades.push(modalidade);
              }
            });

            return acc;
          }, {}),
        );

        const listaMantenedoras = Array.isArray(dadosMantenedora)
          ? dadosMantenedora
          : Array.isArray(dadosMantenedora?.mantenedoras)
            ? dadosMantenedora.mantenedoras
            : dadosMantenedora
              ? [dadosMantenedora]
              : [];

        const mantenedorasNormalizadas = listaMantenedoras.map(
          (mantenedora) => ({
            ...mantenedora,

            etapasModalidades: Array.isArray(mantenedora.etapasModalidades)
              ? mantenedora.etapasModalidades
              : [],

            escolas: Array.isArray(mantenedora.escolas)
              ? mantenedora.escolas
              : listaMantenedoras.length === 1
                ? escolasAgrupadas
                : [],
          }),
        );

        setMantenedoras(mantenedorasNormalizadas);
      } catch (error) {
        console.error("Erro ao carregar os dados institucionais:", error);

        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, []);

  const handleOpenDetalhes = (mantenedora) => {
    setMantenedoraSelecionada(mantenedora);
    setModalDetalhesOpen(true);
  };

  const handleOpenAtos = (mantenedora) => {
    setMantenedoraSelecionada(mantenedora);
    setModalAtosOpen(true);
  };

  const mantenedorasFiltradas = mantenedoras.filter((mantenedora) => {
    const termo = normalizarTexto(busca);

    return (
      normalizarTexto(mantenedora.nome).includes(termo) ||
      normalizarTexto(mantenedora.cidade).includes(termo) ||
      normalizarTexto(mantenedora.cnpj).includes(termo)
    );
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Erro: {error}</Typography>;
  }

  return (
    <>
      <TabelaVinculosInstitucionais
        tipo="mantenedoras"
        ativo={ativo}
        itens={mantenedorasFiltradas}
        onDetalhes={handleOpenDetalhes}
        onAtos={handleOpenAtos}
      />

      <ModalMantenedora
        open={modalDetalhesOpen}
        onClose={() => setModalDetalhesOpen(false)}
        mantenedora={mantenedoraSelecionada}
        onOpenAtos={() => {
          setModalDetalhesOpen(false);
          setModalAtosOpen(true);
        }}
      />

      <ModalAtosMantenedora
        open={modalAtosOpen}
        onClose={() => setModalAtosOpen(false)}
      />
    </>
  );
}
